// i cant figure out how to import these from logrocket so imma just yoink em:
interface IRequest {
  reqId: string
  url: string
  headers: { [key: string]: string | undefined }
  body?: string
  method: string
  referrer?: string
  mode?: string
  credentials?: string
}

interface IResponse {
  reqId: string
  status?: number
  headers: { [key: string]: string | undefined }
  body?: string
  method: string
}

// https://docs.logrocket.com/reference/network

const REDACTED = '[redacted]'

/**
 * Processor credentials — Point of Banking merchant keys, Payomi POS keys, Aeropay merchant ids —
 * are posted as validateProcessorCredentials variables while an internal user types them. Keep the
 * operation itself visible for debugging but strip every credential out of it, and drop the whole
 * body if it does not parse into the shape we expect, so an unrecognised one is never recorded.
 */
const redactProcessorCredentials = (body: string): string | undefined => {
  let parsed: unknown
  try {
    parsed = JSON.parse(body)
  } catch {
    return undefined
  }

  const operations = Array.isArray(parsed) ? parsed : [parsed]

  for (const operation of operations) {
    const settings = (
      operation as {
        variables?: { settings?: Record<string, unknown> }
      }
    )?.variables?.settings

    if (!settings || typeof settings !== 'object') return undefined

    if ('key' in settings) settings.key = REDACTED

    const fields = settings.fields
    if (fields && typeof fields === 'object')
      for (const name of Object.keys(fields))
        (fields as Record<string, unknown>)[name] = REDACTED
  }

  return JSON.stringify(parsed)
}

/**
 * The Internal Tools client form posts the write-only Okta client secret as
 * createClient/updateClient variables (settings.ssoClientSecret, nested under client for
 * updates). Redact just the secret so the rest of the operation stays debuggable, and drop
 * the whole body when it does not parse into the expected shape.
 */
const redactSsoClientSecret = (body: string): string | undefined => {
  let parsed: unknown
  try {
    parsed = JSON.parse(body)
  } catch {
    return undefined
  }

  const operations = Array.isArray(parsed) ? parsed : [parsed]

  for (const operation of operations) {
    const variables = (
      operation as {
        variables?: {
          settings?: Record<string, unknown>
          client?: { settings?: Record<string, unknown> }
        }
      }
    )?.variables

    const settings = variables?.client?.settings ?? variables?.settings
    if (!settings || typeof settings !== 'object') return undefined

    if (settings.ssoClientSecret) settings.ssoClientSecret = REDACTED
  }

  return JSON.stringify(parsed)
}

/**
 * A client's EIN, a US tax ID, rides in business-profile and Text intake variables and in
 * clientBusinessProfile responses. Redact every "ein" field wherever it sits so the rest of the
 * operation stays debuggable, and drop the whole body when it does not parse.
 */
const carriesEin = (body?: string): boolean => /"ein"\s*:/i.test(body ?? '')

const redactEin = (body: string): string | undefined => {
  let parsed: unknown
  try {
    parsed = JSON.parse(body)
  } catch {
    return undefined
  }

  const visit = (value: unknown): void => {
    if (Array.isArray(value)) {
      value.forEach(visit)
      return
    }
    if (!value || typeof value !== 'object') return
    const record = value as Record<string, unknown>
    for (const key of Object.keys(record)) {
      if (key.toLowerCase() === 'ein') record[key] = REDACTED
      else visit(record[key])
    }
  }
  visit(parsed)

  return JSON.stringify(parsed)
}

export const requestSanitizer = (request: IRequest): IRequest | null => {
  // logrocket doesnt need to see all our elastic telemetry
  if ((request?.url ?? '').includes('fh-elastic.apm')) return null

  if ((request?.url ?? '').includes('payarc')) return null

  // plaster over the auth header
  for (const key in request.headers)
    if (key.toLowerCase() === 'authorization')
      request.headers[key] = '00000000-0000-0000-0000-000000000000'

  // strip the credentials out of a processor validation without losing the operation
  if (request.body?.toLowerCase?.().includes('validateprocessorcredentials'))
    request.body = redactProcessorCredentials(request.body)

  // strip the write-only Okta client secret out of a client create/update. Match the quoted
  // variables key: the bare substring would also hit isSsoClientSecretSet in the client
  // fragment text and wrongly drop every client/user list query.
  if (request.body?.toLowerCase?.().includes('"ssoclientsecret"'))
    request.body = redactSsoClientSecret(request.body)

  // keep the operation but never a client's EIN
  if (carriesEin(request.body)) request.body = redactEin(request.body as string)

  if (
    request.body?.toLowerCase?.().includes('upsertintegrationtokens') ||
    request.body?.toLowerCase?.().includes('savelucidgreencredentials')
  )
    request.body = undefined

  // hide bodies with password
  if (request.body?.toLowerCase?.().includes('password'))
    request.body = undefined

  // OIDC login and re-auth proofs: the authorization code + PKCE verifier (loginOidc,
  // SsoReauthInput) and Google ID tokens are one-time credentials — hide the whole body.
  // The ssoReauth match also keeps the new PIN out of updateOwnUserPin captures.
  if (request.body?.toLowerCase?.().includes('codeverifier'))
    request.body = undefined

  if (request.body?.toLowerCase?.().includes('ssoreauth'))
    request.body = undefined

  if (request.body?.toLowerCase?.().includes('logingoogle'))
    request.body = undefined

  // hide bodies with ACH info
  if (request.body?.toLowerCase?.().includes('accountnumber'))
    request.body = undefined

  if (request.body?.toLowerCase?.().includes('routingnumber'))
    request.body = undefined

  // just in case, hide credit card (not used yet)
  if (request.body?.toLowerCase?.().includes('creditcard'))
    request.body = undefined

  // AgentAPI token exchange posts the Flowhub session id as subject_token.
  // Keep the URL/method for debugging, but never capture the body.
  const url = (request?.url ?? '').toLowerCase()
  const body = request.body?.toLowerCase?.() ?? ''
  if (url.includes('/oauth/token') || body.includes('subject_token')) {
    request.body = undefined
  }

  // Intercom Messenger identity JWT — anyone with the body can boot as that user.
  // Cover both GraphQL and SDK wire names, even outside Intercom's API hosts.
  let hostname = ''
  try {
    hostname = new URL(url).hostname
  } catch {
    // Relative/empty URLs still receive the credential-body checks below.
  }
  if (
    body.includes('intercomuserjwt') ||
    body.includes('intercom_user_jwt') ||
    hostname === 'intercom.io' ||
    hostname.endsWith('.intercom.io')
  ) {
    request.body = undefined
  }

  // Customer webhook endpoint URLs can carry their own credentials.
  if (body.includes('mywebhook')) request.body = undefined

  // Agent GraphQL can carry customer-adjacent preview payloads.
  if (url.includes('/agentapi/query')) {
    request.body = undefined
  }

  return request
}

export const responseSanitizer = (response: IResponse): IResponse | null => {
  // clientBusinessProfile responses carry the client's EIN
  if (carriesEin(response.body))
    response.body = redactEin(response.body as string)

  if (
    response.body?.toLowerCase?.().includes('token') ||
    response.body?.toLowerCase?.().includes('savelucidgreencredentials')
  )
    response.body = undefined

  // Webhook signing secrets, customer endpoint URLs and receiver errors.
  if (response.body?.toLowerCase?.().includes('mywebhook'))
    response.body = undefined

  if (response.body?.toLowerCase?.().includes('intercomuserjwt'))
    response.body = undefined

  // Public API credential create/rotate responses carry the one-time
  // clientSecret (no "token" in the body, so the check above misses them).
  if (response.body?.toLowerCase?.().includes('clientsecret'))
    response.body = undefined

  // User apiKeys carry regulator usernames, credential fields and masked key
  // and password suffixes. Match the quoted field name so unrelated names that
  // merely contain it are kept.
  if (response.body?.toLowerCase?.().includes('"apikeys"'))
    response.body = undefined

  // Completed-cart errors name the staff member who completed the sale, both in
  // extensions.data.completedBy and in the older server message.
  if (
    response.body?.toLowerCase?.().includes('"completedby"') ||
    response.body?.toLowerCase?.().includes('cart has been completed by')
  )
    response.body = undefined

  // hide bodies with ACH info
  if (response.body?.toLowerCase?.().includes('accountnumber'))
    response.body = undefined

  if (response.body?.toLowerCase?.().includes('routingnumber'))
    response.body = undefined

  // just in case, hide credit card (not used yet)
  if (response.body?.toLowerCase?.().includes('creditcard'))
    response.body = undefined

  // Agent GraphQL responses can include customerIds in tool/preview payloads.
  // LogRocket does not expose the matching request URL here, so redact when the
  // body looks like an agentapi GraphQL envelope.
  const responseBody = response.body?.toLowerCase?.() ?? ''
  if (
    responseBody.includes('sendmessage') ||
    responseBody.includes('ensureagentsession') ||
    responseBody.includes('agentprogress')
  ) {
    response.body = undefined
  }

  return response
}
